/* eslint-disable jsx-a11y/accessible-emoji */
import { convertDateToStringFormat, getFirstValidPrice } from '@bloomays-lib/utils.shared';
import {
  GridColDef,
  GridRowClassNameParams,
  GridRenderCellParams,
  GridValueGetter,
  gridClasses,
} from '@mui/x-data-grid-premium';

import RawDataGrid from './DataGrid';
import { AllMissionGQLItemResult, IMission, BusinessType, IPricing } from '@bloomays-lib/types.shared';
import { styled } from '@mui/material/styles';
import { LoaderSkeleton, ActionButton, Modal } from '@bloomays-lib/ui.shared';
import { useTranslation } from 'react-i18next';
import TagMissionType from './TagMissionType';
import Typography from '@mui/material/Typography';
import PricingHistory from './PricingHistory';
import { useState } from 'react';

export type TableMissionsProps = {
  data: AllMissionGQLItemResult[];
  fetchLoading: boolean;
  pageSize?: number;
};

const TableMissions = ({ data, fetchLoading, pageSize = 50 }: TableMissionsProps) => {
  const { t } = useTranslation(['mission']);
  const [historyPrice, setHistoryPrice] = useState<IPricing[] | undefined>();
  const columns: GridColDef<AllMissionGQLItemResult>[] = [
    {
      field: 'clientSocietyName',
      headerName: t('clientSocietyName'),
      width: 200,
      headerClassName: 'header',
      groupable: true,
      sortable: true,
      filterable: true,
      cellClassName: 'cellStyle',
      valueGetter: (value: GridValueGetter<AllMissionGQLItemResult>, row: AllMissionGQLItemResult) =>
        row.clientSocietyName?.[0],
    },
    {
      field: 'bloomerFullName',
      headerName: t('bloomerFullName'),
      groupable: true,
      sortable: true,
      filterable: true,
      width: 250,
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      valueGetter: (value: GridValueGetter<AllMissionGQLItemResult>, row: AllMissionGQLItemResult) =>
        row.bloomerFullName?.[0],
    },
    {
      field: 'label',
      headerName: t('label'),
      width: 250,
      groupable: true,
      sortable: true,
      filterable: true,
      headerClassName: 'header',
      cellClassName: 'cellStyle',
    },
    {
      field: 'type',
      headerName: t('Type'),
      groupable: true,
      sortable: true,
      filterable: true,
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      type: 'singleSelect',
      valueOptions: [BusinessType.IT, BusinessType.RPO, BusinessType.People],
      renderCell: (params: GridRenderCellParams<AllMissionGQLItemResult>) => {
        if (params.row.type) {
          return <TagMissionType type={params.row.type} />;
        }
      },
    },
    {
      field: 'beginMission',
      headerName: t('beginMission'),
      width: 150,
      groupable: true,
      sortable: true,
      filterable: true,
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      type: 'date',
      valueGetter: (value: GridValueGetter<AllMissionGQLItemResult>, row: AllMissionGQLItemResult) =>
        row?.beginMission ? new Date(row?.beginMission) : undefined,
      valueFormatter: (value: string) => (value ? convertDateToStringFormat(value, 'dd/MM/yyyy') : undefined),
    },
    {
      field: 'endMission',
      headerName: t('endMission'),
      width: 150,
      groupable: true,
      sortable: true,
      filterable: true,
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      type: 'date',
      valueGetter: (value: GridValueGetter<AllMissionGQLItemResult>, row: AllMissionGQLItemResult) =>
        row?.endMission ? new Date(row?.endMission) : undefined,
      valueFormatter: (value: string) => (value ? convertDateToStringFormat(value, 'dd/MM/yyyy') : undefined),
    },
    {
      field: 'bloomerSocietyName',
      headerName: t('bloomerSocietyName'),
      groupable: true,
      sortable: true,
      filterable: true,
      width: 200,
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      valueGetter: (value: GridValueGetter<AllMissionGQLItemResult>, row: AllMissionGQLItemResult) =>
        row.bloomerSocietyName?.[0],
    },
    {
      field: 'isActive',
      headerName: t('isActive'),
      filterable: true,
      groupable: true,
      sortable: true,
      width: 200,
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      type: 'boolean',
    },
    {
      field: 'replacement',
      headerName: t('replacement'),
      filterable: true,
      groupable: true,
      sortable: true,
      width: 200,
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      type: 'boolean',
    },
    {
      field: 'purchase',
      headerName: t('purchase'),
      width: 200,
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      filterable: true,
      sortable: true,
      type: 'number',
      valueGetter: (value: GridValueGetter<AllMissionGQLItemResult>, row: AllMissionGQLItemResult) => {
        return getFirstValidPrice<IPricing>(row.pricing || [], new Date())?.purchasePricePerDayWithoutTaxes || 0;
      },
      renderCell: (params: GridRenderCellParams<AllMissionGQLItemResult>) => {
        return (
          <Typography sx={{ cursor: 'pointer' }} onClick={() => setHistoryPrice(params.row.pricing || [])}>
            {params.value} €
          </Typography>
        );
      },
    },
    {
      field: 'sale',
      headerName: t('sale'),
      width: 200,
      headerClassName: 'header',
      cellClassName: 'cellStyle',
      filterable: true,
      sortable: true,
      type: 'number',
      valueGetter: (value: GridValueGetter<AllMissionGQLItemResult>, row: AllMissionGQLItemResult) => {
        return getFirstValidPrice<IPricing>(row.pricing || [], new Date())?.salePricePerDayWithoutTaxes || 0;
      },
      renderCell: (params: GridRenderCellParams<AllMissionGQLItemResult>) => {
        return (
          <Typography sx={{ cursor: 'pointer' }} onClick={() => setHistoryPrice(params.row.pricing || [])}>
            {params.value} €
          </Typography>
        );
      },
    },
    {
      field: 'details',
      headerName: t('details'),
      width: 90,
      headerClassName: 'header',
      sortable: false,
      groupable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      cellClassName: 'cellStyle',
      type: 'actions',
      renderCell: (params: GridRenderCellParams<AllMissionGQLItemResult>) => {
        if (params.row.recordId) {
          return (
            <>
              <ActionButton
                actionType="Edit"
                size="small"
                color="secondary"
                variant="contained"
                href={`/missions/${params.row?.recordId}`}
                target="_self"
              />
              <ActionButton
                actionType="Preview"
                color="secondary"
                variant="contained"
                href={`/missions/${params.row?.recordId}`}
                target="_blank"
              />
            </>
          );
        }
      },
    },
  ];

  return (
    <div>
      <Modal additionnalCSS={{ width: '50%' }} open={!!historyPrice} onClose={() => setHistoryPrice(undefined)}>
        <Typography variant="h6">👀 Historique des Prix</Typography>
        <PricingHistory prices={historyPrice} />
      </Modal>
      {data?.length === 0 || typeof data === 'undefined' ? (
        <LoaderSkeleton height={400} width={1200} />
      ) : (
        <DataGrid
          uniqueDatagridId="table-missions"
          initialState={{
            sorting: {
              sortModel: [{ field: 'clientSocietyName', sort: 'asc' }],
            },
            pinnedColumns: {
              left: ['details'],
            },
          }}
          fetchLoading={fetchLoading}
          getRowClassName={(params: GridRowClassNameParams) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          hideFooterPagination={false}
          sortingMode="client"
          filterMode="client"
          rows={data}
          getRowId={(row: IMission) => row.recordId as string}
          columns={columns}
          pageSize={pageSize}
          pagination={true}
          sx={{
            width: '100%',
          }}
        />
      )}
    </div>
  );
};

export default TableMissions;

const DataGrid = styled(RawDataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.neutral[10],
  },
}));
